import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { forwardRef, useMemo, createRef, useEffect, createElement } from 'react';
import cn from 'classnames';
import { getNextIndexFromKeycode } from '../../utils/hooks/useListKeyboardNavigation.js';
import { Spinner } from '../Spinner/Spinner.js';
import { useGaudiLocalization } from '../Provider/Provider.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import { useListScrollTo } from '../../utils/hooks/useListScrollTo.js';

var _excluded = ["data", "disabled", "highlighted", "id", "loading", "onChange", "onClick", "onFocus", "onKeyDown", "readOnly", "scrollOnFocus", "state", "value", "multiselect", "selectedIndexes", "allOptionsSelected"],
    _excluded2 = ["children", "icon"];
var getId = function getId(id, value) {
  return id + "_" + value;
};

var getNextEnabledItem = function getNextEnabledItem(event, data, index) {
  var nextIndex = getNextIndexFromKeycode(event.keyCode, data.length, index);

  if (nextIndex) {
    if (nextIndex === index) {
      return index;
    } else if (data[nextIndex] && data[nextIndex].disabled) {
      return getNextEnabledItem(event, data, nextIndex);
    }
  }

  return nextIndex;
};

var ScrollableList = /*#__PURE__*/forwardRef(function ScrollableList(props, ref) {
  var _cn;

  var data = props.data,
      disabled = props.disabled,
      highlighted = props.highlighted,
      id = props.id,
      loading = props.loading,
      setCurrentIndex = props.onChange,
      onClick = props.onClick,
      onFocus = props.onFocus,
      onKeyDown = props.onKeyDown,
      readOnly = props.readOnly,
      _props$scrollOnFocus = props.scrollOnFocus,
      scrollOnFocus = _props$scrollOnFocus === void 0 ? false : _props$scrollOnFocus,
      state = props.state,
      currentIndex = props.value,
      multiselect = props.multiselect,
      _props$selectedIndexe = props.selectedIndexes,
      selectedIndexes = _props$selectedIndexe === void 0 ? [] : _props$selectedIndexe,
      _props$allOptionsSele = props.allOptionsSelected,
      allOptionsSelected = _props$allOptionsSele === void 0 ? false : _props$allOptionsSele,
      otherProps = _objectWithoutPropertiesLoose(props, _excluded);

  var listRef = useProxiedRef(ref);
  var itemRefs = useMemo(function () {
    return data.map(function () {
      return createRef();
    });
  }, [data]);

  var _useGaudiLocalization = useGaudiLocalization(),
      texts = _useGaudiLocalization.texts;

  var _useListScrollTo = useListScrollTo(listRef, itemRefs),
      scrollTo = _useListScrollTo.scrollTo;

  useEffect(function () {
    scrollTo(currentIndex);
  }, [currentIndex]);

  var handleKeyDown = function handleKeyDown(event) {
    var nextIndex = getNextEnabledItem(event, data, currentIndex);

    if (nextIndex !== undefined && nextIndex !== currentIndex) {
      event.preventDefault();
      scrollTo(nextIndex);
      setCurrentIndex(nextIndex);
    }

    if (onKeyDown) {
      event.persist();
      var index = nextIndex !== undefined ? nextIndex : currentIndex;
      onKeyDown(event, index);
    }
  };

  var handleClick = function handleClick(index) {
    return function (event) {
      setCurrentIndex(index);

      if (onClick) {
        event.persist();
        onClick(event, index);
      }
    };
  };

  var handleFocus = function handleFocus(event) {
    if (scrollOnFocus) {
      scrollTo(currentIndex);
    }

    if (onFocus) {
      event.persist();
      onFocus(event);
    }
  };

  var getOptionCheckedState = function getOptionCheckedState(optionValue, index) {
    if (optionValue === '#ALL-OPTIONS#') {
      return allOptionsSelected;
    } else if (!optionValue || !selectedIndexes) {
      return false;
    } else {
      return selectedIndexes.findIndex(function (i) {
        return i === index;
      }) !== -1;
    }
  };

  var list = _extends({}, otherProps, {
    className: cn('yt-list', 'yt-input', (_cn = {}, _cn["yt-input--state-" + state] = !!state, _cn['yt-input--highlighted'] = highlighted, _cn['yt-list--multiselect'] = multiselect, _cn), otherProps.className),
    disabled: disabled,
    id: id,
    onFocus: !disabled && !readOnly ? handleFocus : undefined,
    onKeyDown: !disabled && !readOnly ? handleKeyDown : undefined,
    readOnly: readOnly,
    ref: listRef,
    role: 'listbox',
    tabIndex: otherProps.tabIndex || 0
  });

  var options = data.map(function (option, index) {
    var depth = option.path ? option.path.split('.').length - 1 : 0;
    return {
      'aria-selected': multiselect ? getOptionCheckedState(String(option.value), index) : currentIndex === index,
      'data-focused': currentIndex === index,
      children: option.text,
      className: depth === 0 && !!option.hasChildren ? 'sticky top-0 font-bold' : undefined,
      disabled: option.disabled,
      icon: option.icon,
      id: getId(id, option.value),
      key: getId(id, option.value),
      onClick: !disabled && !readOnly ? handleClick(index) : undefined,
      ref: itemRefs[index],
      role: 'option',
      style: depth > 0 ? {
        paddingLeft: depth + 1 + "rem"
      } : undefined
    };
  });
  return createElement("ul", Object.assign({}, list), loading ? createElement("li", {
    className: "yt-list__empty"
  }, createElement("span", null, createElement(Spinner, {
    delay: 0
  })), createElement("span", null, texts.listbox.loading)) : options.length ? options.map(function (_ref) {
    var children = _ref.children,
        icon = _ref.icon,
        optionProps = _objectWithoutPropertiesLoose(_ref, _excluded2);

    return createElement("li", Object.assign({}, optionProps), icon, createElement("span", {
      className: "flex-grow text-left"
    }, children), multiselect && createElement(Checkbox, {
      checked: optionProps['aria-selected'],
      onChange: function onChange() {
        return null;
      },
      className: "yt-input-checkbox"
    }));
  }) : createElement("li", {
    className: "yt-list__empty"
  }, createElement("span", null, texts.listbox.empty)));
});

export { ScrollableList, getId };
//# sourceMappingURL=ScrollableList.js.map
